import { apiClient } from './client'
import type { PackagesResponse } from '@/types/domain'

/** What an account has used and what it is allowed. */
export interface PlanUsage {
  plan: string
  pages: number
  maxPages: number
  templates: number
  maxTemplates: number
  retentionDays: number
  /** Absent when the plan is unlimited, so "none left" and "no limit" are
   *  distinguishable without inspecting `maxPages`. */
  pagesRemaining?: number
}

export interface UsageResponse {
  plan: string
  /** False when this deployment sells no self-service: there are no limits
   *  to show, and inventing them would promise enforcement that does not
   *  happen. */
  enforced: boolean
  usage?: PlanUsage
}

export const billingApi = {
  async usage(): Promise<UsageResponse> {
    const { data } = await apiClient.axios.get<UsageResponse>('/v1/billing/usage')
    return data
  },

  /**
   * What can be bought, and the caller's credit balance beside it.
   *
   * A package is `{key, priceId}` and nothing else: the gateway deliberately
   * keeps the amount, the credit count, the currency and the display name in
   * Stripe, so nothing here can disagree with what is charged. **A billing
   * screen is therefore not buildable against this endpoint yet** — it can
   * offer a key and start a checkout, but it cannot show a price or say how
   * many credits a box holds without reading Stripe, which the gateway does
   * not expose. Anything more would have to be invented in the client, which
   * is exactly the number nobody should see next to a price.
   *
   * `forSale: false` with an empty list is the honest answer on an
   * invoiced-only deployment; the caller hides the buy button.
   */
  async packages(): Promise<PackagesResponse> {
    const { data } = await apiClient.axios.get<PackagesResponse>('/v1/billing/packages')
    return { ...data, packages: data.packages ?? [] }
  },

  /**
   * Starts a purchase and returns the URL to send the browser to.
   *
   * `priceId` must be one of the offers from `packages()`: the gateway
   * refuses any other price id (400 `unknown_package`), so a stale offer
   * fails loudly rather than charging a price meant for someone else.
   * On a deployment that sells nothing this is 404 `not_for_sale`.
   */
  async checkout(priceId: string): Promise<string> {
    const { data } = await apiClient.axios.post<{ url?: string }>('/v1/billing/checkout', {
      priceId,
    })
    return data.url ?? ''
  },
}

/**
 * The two refusals a plan produces, as the gateway answers them: 402 with a
 * code, because the app offers different things for each — an upgrade for
 * pages, deleting a form for templates.
 */
export type LimitCode = 'page_limit_reached' | 'template_limit_reached'

export interface LimitRefusal {
  code: LimitCode
  plan: string
  used: number
  limit: number
}

/** Reads a plan refusal out of a caught error, or null if it is not one. */
export function asLimitRefusal(error: unknown): LimitRefusal | null {
  const details = (error as { status?: number; details?: Record<string, unknown> } | null) ?? null
  if (details?.status !== 402) return null
  const code = details.details?.code
  if (code !== 'page_limit_reached' && code !== 'template_limit_reached') return null
  return {
    code,
    plan: String(details.details?.plan ?? ''),
    used: Number(details.details?.used ?? 0),
    limit: Number(details.details?.limit ?? 0),
  }
}
